import { EditorWatermarkCommittedEdit } from '../../domain/editor-domain.models';
import { toWatermarkCssFontFamily } from '../../domain/models/watermark-fonts';

export type EditorWatermarkParams = EditorWatermarkCommittedEdit['params'];
import { clamp, parseHexColor } from './canvas-color.utils';
import { cloneCanvas } from './canvas-dom.utils';

export function applyWatermark(
  document: Document,
  sourceCanvas: HTMLCanvasElement,
  params: EditorWatermarkParams,
): HTMLCanvasElement {
  const text = params.text.trim();
  if (!text) {
    return cloneCanvas(document, sourceCanvas);
  }

  const target = cloneCanvas(document, sourceCanvas);
  const ctx = target.getContext('2d');

  if (!ctx) {
    return sourceCanvas;
  }

  const canvasSize = getWatermarkCanvasSize(target);
  let fontSize = resolveWatermarkFontSize(params.fontSize, canvasSize);
  const fontFamily = toWatermarkCssFontFamily(params.fontName);
  const textColor = parseHexColor(params.textColor || '#000000', [0, 0, 0, 255]);
  const textOpacity = clamp((textColor[3] / 255) * 0.5, 0, 1);
  const renderFont = (size: number) => `700 ${Math.max(1, size)}px ${fontFamily}`;

  ctx.save();
  ctx.fillStyle = `rgba(${textColor[0]}, ${textColor[1]}, ${textColor[2]}, ${textOpacity})`;
  ctx.textBaseline = 'top';
  ctx.font = renderFont(fontSize);

  let textMetrics = measureWatermarkText(ctx, text, fontSize);

  if (textMetrics.width < canvasSize / 3) {
    const rowGap = fontSize * 2.75;
    const columnGap = fontSize * 2.75;

    ctx.translate(target.width / 2, target.height / 2);
    ctx.rotate(degreesToRadians(params.rotationAngleDegrees));
    ctx.translate(-canvasSize / 2, -canvasSize / 2);

    let top = 0;
    let oddRow = false;

    while (top < canvasSize) {
      let left = oddRow ? -(textMetrics.width + columnGap) / 2 : 0;

      while (left < canvasSize) {
        ctx.fillText(text, left, top);
        left += textMetrics.width + columnGap;
      }

      top += textMetrics.height + rowGap;
      oddRow = !oddRow;
    }
  } else {
    ctx.translate(target.width / 2, target.height / 2);
    ctx.rotate(degreesToRadians(params.rotationAngleDegrees));

    while (textMetrics.width > canvasSize * 0.7 && fontSize > 4) {
      fontSize -= 0.3;
      ctx.font = renderFont(fontSize);
      textMetrics = measureWatermarkText(ctx, text, fontSize);
    }

    ctx.fillText(text, -textMetrics.width / 2, -textMetrics.height / 2);
  }

  ctx.restore();

  return target;
}

function getWatermarkCanvasSize(canvas: HTMLCanvasElement): number {
  return Math.sqrt(canvas.width * canvas.width + canvas.width * canvas.height);
}

export function resolveWatermarkFontSize(fontSize: number, canvasSize: number): number {
  if (fontSize > 0) {
    return fontSize;
  }

  return (Math.max(canvasSize, 550) / 1000) * 20;
}

function measureWatermarkText(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontSize: number,
): { width: number; height: number } {
  const metrics = ctx.measureText(text);
  const measuredHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

  return {
    width: metrics.width,
    height: measuredHeight > 0 ? measuredHeight : fontSize,
  };
}

function degreesToRadians(value: number): number {
  return (value * Math.PI) / 180;
}
